import React, { useState, useEffect, useCallback } from 'react';
import Button from '../Button/Button';
import OtpVerification from '../../pages/Publications/components/SignatureContrat/OtpVerification';
import SignatureCanvas from '../../pages/Publications/components/SignatureContrat/SignatureCanvas';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import '../../pages/Publications/components/SignatureContrat/SignatureFlow.css';

interface SignataireInfo {
    nom: string;
    prenom: string;
    email: string;
    telephone?: string;
    fonction?: string;
}

interface SignatureFlowProps {
    /** ID de l'opération (marché subséquent ou bon de commande) */
    operationId: number;
    /** ID de la commande à signer */
    commandeId: number;
    /** Préfixe de route : 'subsequents' | 'bdc' | 'bdci' */
    resourcePrefix?: string;
    /** URL de signature personnalisée (remplace ${baseUrl}/signer) */
    signerUrl?: string;
    /** Titre affiché dans l'état success (optionnel) */
    successTitle?: string;
    /** Corps du message affiché dans l'état success (optionnel) */
    successBody?: string;
    /** Nom pré-rempli depuis le parent (évite de l'afficher dans la modale) */
    initialNomComplet?: string;
    /** Si true, masque la section "Modifier le nom" dans la modale */
    hideNameEdit?: boolean;
    /** Si false, l'étape OTP (vérification par code) est ignorée : signature directe nom + dessin.
     *  Défaut : true pour MS/AC, false pour le BDC (déduit du resourcePrefix). */
    requireOtp?: boolean;
    /** Données supplémentaires à fusionner dans le body de la requête de signature (ex: submissionData candidature) */
    additionalPayload?: Record<string, any>;
    onClose: () => void;
    onSigned: () => void;
}

type FlowState = 'loading' | 'ready' | 'signing' | 'success' | 'error';

export default function SignatureFlow({
    operationId,
    commandeId,
    resourcePrefix = 'subsequents',
    signerUrl,
    successTitle,
    successBody,
    initialNomComplet,
    hideNameEdit = false,
    additionalPayload,
    requireOtp: requireOtpProp,
    onClose,
    onSigned,
}: SignatureFlowProps) {
    const isBdc = resourcePrefix.startsWith('bdc');
    // Vérification par code (OTP) requise par défaut pour MS/AC ; ignorée pour le BDC
    // (signature = nom du signataire + dessin manuscrit, sans code).
    const requireOtp = requireOtpProp ?? !isBdc;
    const labelDocument = isBdc ? 'bon de commande' : 'marché';
    const labelDocumentCap = isBdc ? 'Bon de commande' : 'Marché';
    const [flowState, setFlowState] = useState<FlowState>('loading');
    const [signataire, setSignataire] = useState<SignataireInfo | null>(null);
    const [telephoneValide, setTelephoneValide] = useState(false);
    const [otpToken, setOtpToken] = useState<string | null>(null);
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [editNomComplet, setEditNomComplet] = useState('');
    // Le nom doit être validé avant de débloquer la signature manuscrite.
    // Si le nom est masqué (pré-rempli par le parent), on considère qu'il est déjà validé.
    const [nameValidated, setNameValidated] = useState(hideNameEdit);

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    const baseUrl = `${apiUrl}/${resourcePrefix}/signature/${operationId}`;

    // Bloquer le scroll du body pendant que la modale est ouverte
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Fetch signataire info on mount
    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const res = await fetch(`${baseUrl}/signataire-info`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (!res.ok) {
                    setErrorMessage(data.message || 'Impossible de récupérer les informations du signataire.');
                    setFlowState('error');
                    return;
                }
                setSignataire(data.signataire);
                setEditNomComplet(initialNomComplet?.trim() || `${data.signataire.prenom || ''} ${data.signataire.nom || ''}`.trim());
                const phone = data.signataire?.telephone || '';
                setTelephoneValide(/^(06|07)\d{8}$/.test(phone.replace(/[\s.\-]/g, '')));
                setFlowState('ready');
            } catch {
                setErrorMessage('Impossible de contacter le serveur.');
                setFlowState('error');
            }
        };
        fetchInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationId]);

    const handleOtpVerified = useCallback((tokenSig: string) => {
        setOtpToken(tokenSig);
    }, []);

    const handleSignatureChange = useCallback((base64: string | null, _type: 'texte' | 'dessin') => {
        setSignatureData(base64);
    }, []);

    const handleTelephoneUpdated = useCallback((newPhone: string) => {
        setSignataire((prev) => (prev ? { ...prev, telephone: newPhone } : prev));
        setTelephoneValide(true);
    }, []);

    const canSign = (!requireOtp || !!otpToken) && nameValidated && !!editNomComplet.trim() && !!signatureData;

    const handleClose = useCallback(() => {
        if (otpSent && flowState !== 'success') {
            setShowLeaveConfirm(true);
        } else {
            onClose();
        }
    }, [otpSent, flowState, onClose]);

    const handleSign = async () => {
        if ((requireOtp && !otpToken) || !signatureData) return;
        setFlowState('signing');
        setErrorMessage('');
        try {
            const res = await fetch(signerUrl ?? `${baseUrl}/signer`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...(requireOtp ? { token_signature: otpToken } : {}),
                    signature_image: signatureData,
                    commande_id: commandeId,
                    signataire: {
                        nom: editNomComplet.split(' ').slice(1).join(' '),
                        prenom: editNomComplet.split(' ')[0] || '',
                        email: signataire?.email,
                    },
                    ...(additionalPayload ?? {}),
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setErrorMessage(data.message || 'Erreur lors de la signature.');
                setFlowState('ready');
                return;
            }
            setFlowState('success');
        } catch {
            setErrorMessage('Impossible de contacter le serveur.');
            setFlowState('ready');
        }
    };

    const handleSuccessClose = () => {
        onSigned();
        onClose();
    };

    return (
        <>
            {/* Pas de fermeture au clic sur le fond : seule la croix ferme la modale */}
            <div className="signature-modal-backdrop">
                <div className="signature-modal-panel">
                    {/* Header */}
                    <div className="signature-modal-header">
                        <h2>Signer le {labelDocument}</h2>
                        <button
                            className="modal-close-btn"
                            onClick={handleClose}
                            aria-label="Fermer"
                        >
                            ×
                        </button>
                    </div>

                    {/* Body */}
                    <div className="signature-modal-body">
                        {flowState === 'loading' && (
                            <div className="signature-modal-loading">
                                <div className="spinner" />
                                <p>Chargement…</p>
                            </div>
                        )}

                        {flowState === 'error' && (
                            <div className="signature-modal-error">
                                <p>{errorMessage}</p>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    value="Fermer"
                                    onClick={handleClose}
                                />
                            </div>
                        )}

                        {(flowState === 'ready' || flowState === 'signing') && signataire && (
                            <>
                                {/* Step 1 : OTP (vérification par code) — ignorée pour le BDC */}
                                {requireOtp && (
                                    <>
                                        <OtpVerification
                                            candidatureId={operationId}
                                            email={signataire.email}
                                            telephone={signataire.telephone}
                                            telephoneValide={telephoneValide}
                                            onVerified={handleOtpVerified}
                                            onTelephoneUpdated={handleTelephoneUpdated}
                                            onOtpSent={() => setOtpSent(true)}
                                            sendOtpUrl={`${baseUrl}/demander-otp`}
                                            verifyOtpUrl={`${baseUrl}/verifier-otp`}
                                        />

                                        <div className="signature-modal-separator" />
                                    </>
                                )}

                                {/* Nom du signataire : champ direct, à valider avant de débloquer la signature */}
                                {!hideNameEdit && (
                                    <div className="signature-identity-section">
                                        <label className="signature-identity-label" htmlFor="signataire-nom-input">
                                            Nom du signataire
                                        </label>
                                        <div className="signature-identity-fields">
                                            <input
                                                id="signataire-nom-input"
                                                className="signature-identity-input-large"
                                                type="text"
                                                value={editNomComplet}
                                                placeholder="Prénom Nom"
                                                onChange={(e) => {
                                                    setEditNomComplet(e.target.value);
                                                    setNameValidated(false);
                                                }}
                                                maxLength={80}
                                            />
                                            <Button
                                                type="button"
                                                variant="primary"
                                                value={nameValidated ? 'Nom validé' : 'Valider le nom'}
                                                onClick={() => setNameValidated(true)}
                                                disabled={!editNomComplet.trim() || nameValidated}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="signature-modal-separator" />

                                {/* Step 2 : Dessin signature — verrouillé tant que le nom n'est pas validé */}
                                {!nameValidated && (
                                    <p className="signature-canvas-lock-hint">
                                        Validez le nom du signataire pour pouvoir signer à la main.
                                    </p>
                                )}
                                <div className={`signature-canvas-lockwrap${!nameValidated ? ' is-locked' : ''}`}>
                                    <SignatureCanvas
                                        signataireName={editNomComplet}
                                        signataireFunction={signataire.fonction}
                                        onSignatureChange={handleSignatureChange}
                                    />
                                </div>

                                {errorMessage && (
                                    <div className="signature-modal-inline-error">
                                        {errorMessage}
                                    </div>
                                )}

                                <div className="signature-modal-actions">
                                    <Button
                                        type="button"
                                        variant="primary"
                                        value={
                                            flowState === 'signing'
                                                ? 'Signature en cours…'
                                                : `SIGNER LE ${labelDocumentCap.toUpperCase()}`
                                        }
                                        onClick={handleSign}
                                        disabled={!canSign || flowState === 'signing'}
                                    />
                                </div>
                            </>
                        )}

                        {flowState === 'success' && (
                            <div className="signature-modal-success">
                                <div className="signature-modal-success-icon">✓</div>
                                <h3>{successTitle ?? `${labelDocumentCap} signé avec succès !`}</h3>
                                <p>
                                    {successBody ?? "Votre signature a été enregistrée. Un accusé de réception vous a été envoyé par email."}
                                </p>
                                <div className="signature-modal-success-actions">
                                    <Button
                                        type="button"
                                        variant="primary"
                                        value="Fermer"
                                        onClick={handleSuccessClose}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ValidationPopUp
                isOpen={showLeaveConfirm}
                title="Signature en cours"
                text="Vous avez commencé le processus de signature. Si vous quittez, vous devrez redemander un code. Êtes-vous sûr de vouloir quitter ?"
                cancelLabel="Rester"
                confirmLabel="Quitter"
                onClose={() => setShowLeaveConfirm(false)}
                onConfirm={() => {
                    setShowLeaveConfirm(false);
                    onClose();
                }}
            />
        </>
    );
}
